import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from "react";
import useSWR, { useSWRConfig } from "swr";
import { Alert, AlertActions, AlertBody, AlertDescription, AlertTitle, } from "catalyst/alert";
import { Button } from "catalyst/button";
import { Text } from "catalyst/text";
import { accountErrorMessage, AccountApiError, createAccountApi, } from "./account-api.js";
import { CopyableValue } from "./copyable-value.js";
const AccountContext = createContext(null);
const shouldRetryOnError = (error) => !(error instanceof AccountApiError && error.status === 401);
export function useAccount() {
    const account = useContext(AccountContext);
    if (!account)
        throw new Error("AccountProvider is required");
    return account;
}
export function AccountProvider({ hubOrigin = "", decorateRequestUrl, onLoggedOut, onUnauthorized, onProfileChanged, children, }) {
    const callbacks = useRef({ onUnauthorized, onProfileChanged });
    callbacks.current = { onUnauthorized, onProfileChanged };
    const mounted = useRef(true);
    const unauthorizedReported = useRef(false);
    useLayoutEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const api = useMemo(() => createAccountApi(hubOrigin, () => {
        if (!mounted.current || unauthorizedReported.current)
            return;
        unauthorizedReported.current = true;
        callbacks.current.onUnauthorized?.();
    }, decorateRequestUrl), [hubOrigin, decorateRequestUrl]);
    const profileResponse = useSWR(api.keys.profile, () => api.fetchProfile(), { shouldRetryOnError });
    const [confirmingLogout, setConfirmingLogout] = useState(false);
    useEffect(() => {
        if (profileResponse.data)
            callbacks.current.onProfileChanged?.(profileResponse.data);
    }, [profileResponse.data]);
    return (_jsxs(AccountContext.Provider, { value: {
            api,
            profile: profileResponse.data,
            requestLogout: () => setConfirmingLogout(true),
        }, children: [children, confirmingLogout && (_jsx(LogoutDialog, { onClose: () => setConfirmingLogout(false), onLoggedOut: onLoggedOut }))] }));
}
function LogoutDialog({ onClose, onLoggedOut, }) {
    const { api } = useAccount();
    const { mutate } = useSWRConfig();
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState(null);
    const [attempt, setAttempt] = useState(0);
    const [hasCopiedLoginLink, setHasCopiedLoginLink] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    useEffect(() => {
        const controller = new AbortController();
        setProfile(null);
        setError(null);
        setHasCopiedLoginLink(false);
        // Refresh login methods before offering logout without saving the secret link.
        void api
            .fetchProfile(controller.signal)
            .then(async (profile) => {
            if (controller.signal.aborted)
                return;
            if (typeof profile?.login_link !== "string" ||
                typeof profile.has_passkey !== "boolean" ||
                (profile.verified_email !== null &&
                    typeof profile.verified_email !== "string"))
                throw new Error("Incomplete account recovery data");
            if (!profile.login_link.trim() &&
                !profile.has_passkey &&
                !profile.verified_email)
                throw new AccountApiError(200, "Не удалось получить ссылку для восстановления доступа. Попробуйте еще раз.");
            await mutate(api.keys.profile, profile, { revalidate: false });
            if (controller.signal.aborted)
                return;
            setProfile(profile);
        })
            .catch((reason) => {
            if (!controller.signal.aborted)
                setError(accountErrorMessage(reason));
        });
        return () => controller.abort();
    }, [api, mutate, attempt]);
    const canLogout = profile !== null &&
        (profile.has_passkey ||
            Boolean(profile.verified_email) ||
            (Boolean(profile.login_link.trim()) && hasCopiedLoginLink));
    const logout = async () => {
        if (isLoggingOut || !canLogout)
            return;
        setIsLoggingOut(true);
        setError(null);
        try {
            await api.logout().catch((reason) => {
                // A session ended in another tab is already logged out on the server.
                if (!(reason instanceof AccountApiError && reason.status === 401))
                    throw reason;
            });
            onLoggedOut();
            onClose();
        }
        catch (reason) {
            setError(accountErrorMessage(reason));
            setIsLoggingOut(false);
        }
    };
    return (_jsxs(Alert, { open: true, onClose: () => {
            if (!isLoggingOut)
                onClose();
        }, size: "lg", children: [_jsx(AlertTitle, { children: "\u0412\u044B\u0445\u043E\u0434 \u0438\u0437 \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430" }), _jsx(AlertDescription, { children: profile
                    ? profile.verified_email
                        ? "После выхода вы сможете снова войти по коду из письма. Секретная ссылка также останется доступна."
                        : profile.has_passkey
                            ? "После выхода вы сможете снова войти по passkey или секретной ссылке."
                            : "Скопируйте и сохраните ссылку, чтобы иметь возможность восстановить доступ к аккаунту после выхода."
                    : "Проверяем способы восстановления доступа к аккаунту." }), _jsxs(AlertBody, { children: [profile?.login_link.trim() ? (_jsx(CopyableValue, { value: profile.login_link, inputAriaLabel: "\u0421\u0435\u043A\u0440\u0435\u0442\u043D\u0430\u044F \u0441\u0441\u044B\u043B\u043A\u0430", onCopy: () => setHasCopiedLoginLink(true) })) : profile ? (_jsx(Text, { children: "\u0421\u0435\u043A\u0440\u0435\u0442\u043D\u0430\u044F \u0441\u0441\u044B\u043B\u043A\u0430 \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u043D\u0430." })) : !error ? (_jsx(Text, { role: "status", children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430..." })) : null, error && (_jsx(Text, { role: "alert", className: "text-red-600! dark:text-red-500!", children: error }))] }), _jsxs(AlertActions, { children: [_jsx(Button, { plain: true, disabled: isLoggingOut, onClick: onClose, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), !profile && error && (_jsx(Button, { onClick: () => setAttempt((value) => value + 1), children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" })), _jsx(Button, { color: "orange", disabled: isLoggingOut || !canLogout, onClick: () => void logout(), children: isLoggingOut ? "Выходим…" : "Выйти на этом устройстве" })] })] }));
}
